import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeExpensesSubheadStatus, fetchExpensesHeads, fetchExpensesSubheads, saveExpensesSubhead } from "store/actions";
import Loader from "common/pageLoader";
import Swal from "sweetalert2";
import { showCancelConfirmation } from "common/Confirmationmessage";

const ExpenseMasterSubHeads = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    masterheads,
    mastersubheads,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    masterheads: state.ExpensesMasterReducer?.masterheads ?? [],
    mastersubheads: state.ExpensesMasterReducer?.mastersubheads ?? [],

    loading: state.ExpensesMasterReducer?.loading,
    error: state.ExpensesMasterReducer?.error,
    success: state.ExpensesMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchExpensesHeads(unitId));
    dispatch(fetchExpensesSubheads(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchExpensesSubheads(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const formik = useFormik({
    initialValues: {
      headName: "",
      subHeadsName: "",
    },
    validationSchema: Yup.object({
      headName: Yup.object().nullable().required("Head Name is required"),
      subHeadsName: Yup.string().trim().required("Please enter Sub Head Name").test("no-whitespace-only", "Please enter Sub Head Name", val => val && val.trim().replace(/\s+/g, "") !== "").max(500, "Sub Head Name cannot exceed 500 characters").test("no-duplicate", "Sub Head Name already exists for this Head", function (val) {
        if (!val) return true;
        const { headName } = this.parent;
        const trimmed = val.trim().toLowerCase();
        return !mastersubheads.some(
          (item) =>
            item.subhead_name.trim().toLowerCase() === trimmed &&
            item.head_id === headName?.value &&
            item.subhead_id !== editId
        );
      }),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("expensessubhead_id", editId ? editId : 0);
      formData.append("subhead_name", values.subHeadsName.trim());
      formData.append("head_id", values.headName?.value);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveExpensesSubhead(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      headName: { value: row.head_id, label: row.heads_name },
      subHeadsName: row.subhead_name,
    });
    setIsEdit(true);
    setEditId(row.subhead_id);
  };

  const handleCancel = () => {
    if (formik.dirty || isEdit) {
      showCancelConfirmation(() => {
        formik.resetForm();
        setIsEdit(false);
        setEditId(null);
      });
    } else {
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  };


  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => mastersubheads.length - row.index,
      className: "serial-no text-center",
    },

     {
      Header: "Action",
      className: "action-col",
      applyHeaderClass: true,
      Cell: (cellProps) => {
        const { b_active, subhead_id } = cellProps.row.original;
        const isActive = b_active === 1;
        return (
          <div className="d-flex gap-3">
            <Link
              to="#"
              style={{ color: isActive ? "green" : "red" }}
              onClick={() => handleToggleStatus(subhead_id, isActive)}
            >
              <i
                className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                id={`status-tooltip-${subhead_id}`}
              />
              <UncontrolledTooltip placement="top" target={`status-tooltip-${subhead_id}`}>
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-success"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit font-size-18" id={`edittooltip-${subhead_id}`} />
              <UncontrolledTooltip placement="top" target={`edittooltip-${subhead_id}`}>
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },

    
    {
      Header: "Heads Name",
      accessor: "heads_name",
    },
    { Header: "Sub Heads Name", accessor: "subhead_name", },
   
  ];

  const handleToggleStatus = (subhead_id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("expensessubhead_id", subhead_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeExpensesSubheadStatus(formData));
      }
    });
  };


  return (
    <div className="page-content">
      <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItem="Sub Heads" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label for="headName">
                      Heads Name <span className="text-danger">*</span>
                    </Label>
                    <CustomSelect
                      id="headName"
                      name="headName"
                      options={(masterheads || []).map(emp => ({ value: emp.exp_id, label: emp.expense_name }))}
                      value={formik.values.headName || null}
                      onChange={(selectedOption) => formik.setFieldValue("headName", selectedOption)}
                    />
                    {formik.touched.headName && formik.errors.headName && (
                      <div className="text-danger">{formik.errors.headName}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="subHeadsName">
                      Sub Heads Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="subHeadsName"
                      name="subHeadsName"
                      type="text"
                      maxLength={500}
                      value={formik.values.subHeadsName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.subHeadsName &&
                        Boolean(formik.errors.subHeadsName)
                      }
                    />
                    {formik.touched.subHeadsName && formik.errors.subHeadsName && (
                      <div className="text-danger">{formik.errors.subHeadsName}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-center justify-content-end mt-4">
                  <Button type="submit" className="btn button-save me-2">
                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                  </Button>
                  <Button type="button" className="btn button-cancel" onClick={handleCancel}><i className="fas fa-sync me-1" /> Cancel</Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={mastersubheads}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ExpenseMasterSubHeads;
